import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Building2, Plus, Trash2, MapPin, Verified, Search } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * Club venues manager — attach/detach verified facilities to a club.
 * Only verified facilities can be linked (admin/federation can bypass).
 *
 * Props:
 *   - clubs: array of club docs the user can manage
 *   - isAdmin: boolean (bypasses the verified-only gate server-side too)
 */
export default function ClubVenuesManager({ clubs = [], isAdmin = false }) {
  const [selectedClubId, setSelectedClubId] = useState(clubs[0]?.id || "");
  const [linked, setLinked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!selectedClubId && clubs[0]) setSelectedClubId(clubs[0].id);
  }, [clubs, selectedClubId]);

  const loadLinked = useCallback(async () => {
    if (!selectedClubId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/clubs/${selectedClubId}/facilities`);
      setLinked(data.facilities || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load linked venues");
    } finally { setLoading(false); }
  }, [selectedClubId]);

  useEffect(() => { loadLinked(); }, [loadLinked]);

  const search = async () => {
    if (!query || query.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      // Only verified facilities are eligible for linking (admin can bypass)
      const params = new URLSearchParams({ q: query, limit: "20" });
      const { data } = await api.get(`/facilities?${params.toString()}`);
      const rows = data.results || [];
      const linkedIds = new Set(linked.map((f) => f.id));
      setResults(rows.filter((r) => !linkedIds.has(r.id)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSearching(false); }
  };

  const attach = async (facility) => {
    if (!facility.verified && !isAdmin) {
      toast.error("Only verified facilities can be linked. Ask an admin to verify this venue first.");
      return;
    }
    try {
      await api.post(`/clubs/${selectedClubId}/facilities/${facility.id}`);
      toast.success(`Linked "${facility.name}"`);
      setResults((rs) => rs.filter((r) => r.id !== facility.id));
      loadLinked();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const detach = async (facility) => {
    if (!window.confirm(`Remove "${facility.name}" from this club's venues?`)) return;
    try {
      await api.delete(`/clubs/${selectedClubId}/facilities/${facility.id}`);
      toast.success("Removed");
      loadLinked();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (clubs.length === 0) {
    return (
      <div className="pp-card p-6 text-center text-zinc-500 text-sm" data-testid="club-venues-empty">
        <Building2 size={24} className="mx-auto mb-2 text-zinc-700" />
        You don't manage any clubs yet.
      </div>
    );
  }

  return (
    <div className="pp-card-elevated p-6 space-y-4" data-testid="club-venues-manager">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Building2 size={18} className="text-[#00E676]" />
          <h3 className="font-heading text-lg font-bold">Club venues</h3>
        </div>
        {clubs.length > 1 && (
          <select
            value={selectedClubId}
            onChange={(e) => setSelectedClubId(e.target.value)}
            className="pp-input text-xs"
            data-testid="club-venues-club-select"
          >
            {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
      </div>
      <p className="text-xs text-zinc-500">
        Link verified venues to this club. Members see them on the club page, and events created for the club surface them so players always know where they're playing.
      </p>

      {/* Linked venues */}
      <div className="space-y-2" data-testid="club-venues-linked">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">
          Linked venues · {loading ? "…" : linked.length}
        </div>
        {linked.length === 0 && !loading && (
          <div className="text-xs text-zinc-500 italic">No venues linked yet. Search below to add one.</div>
        )}
        {linked.map((f) => (
          <div key={f.id} className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-white/[0.03] border border-white/5" data-testid={`cv-linked-${f.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium truncate">{f.name}</span>
                {f.verified && <Verified size={12} className="text-[#00E676]" />}
                {(f.sport_slugs || []).slice(0, 3).map((s) => (
                  <span key={s} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-400 uppercase">{s}</span>
                ))}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1">
                <MapPin size={10} /> {[f.city, f.country].filter(Boolean).join(", ") || "—"}
              </div>
            </div>
            <button
              onClick={() => detach(f)}
              data-testid={`cv-detach-${f.id}`}
              title="Remove from club"
              className="p-1.5 rounded-lg border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10 shrink-0"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>

      {/* Search + attach */}
      <div className="space-y-2 pt-3 border-t border-white/5">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">Add a venue</div>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search verified venues by name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())}
            data-testid="cv-search-input"
            className="pp-input flex-1"
          />
          <button
            onClick={search}
            disabled={searching || query.length < 2}
            data-testid="cv-search-btn"
            className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center gap-1 disabled:opacity-50"
          >
            <Search size={12} /> {searching ? "…" : "Search"}
          </button>
        </div>
        {results.length > 0 && (
          <div className="space-y-1 max-h-64 overflow-auto" data-testid="cv-results">
            {results.map((f) => (
              <button
                key={f.id}
                onClick={() => attach(f)}
                disabled={!f.verified && !isAdmin}
                data-testid={`cv-attach-${f.id}`}
                className="w-full flex items-center justify-between gap-2 p-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate">{f.name}</span>
                    {f.verified ? (
                      <Verified size={12} className="text-[#00E676]" />
                    ) : (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FFD60A]/10 text-[#FFD60A] uppercase">unverified</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {[f.city, f.country, (f.sport_slugs || []).join(", ")].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div className="shrink-0 text-[#00E676] flex items-center gap-1 text-xs">
                  <Plus size={12} /> Link
                </div>
              </button>
            ))}
          </div>
        )}
        {query.length >= 2 && !searching && results.length === 0 && (
          <div className="text-xs text-zinc-500 italic">No matches. Try a broader search.</div>
        )}
      </div>
    </div>
  );
}
